import { Component } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

type GradientType = 'linear' | 'radial' | 'conic';

interface Stop {
    color: string;
    position: number;
}

interface State {
    type: GradientType;
    angle: number;
    shape: 'circle' | 'ellipse';
    stops: Stop[];
    copyKind: 'css' | 'svg' | null;
}

function buildGradient(type: GradientType, angle: number, shape: string, stops: Stop[]): string {
    const sortedStops = [...stops].sort((a, b) => a.position - b.position);
    const stopStr = sortedStops.map(s => `${s.color} ${s.position}%`).join(', ');
    if (type === 'linear') return `linear-gradient(${angle}deg, ${stopStr})`;
    if (type === 'radial') return `radial-gradient(${shape}, ${stopStr})`;
    return `conic-gradient(from ${angle}deg, ${stopStr})`;
}

function buildSvg(type: GradientType, angle: number, stops: Stop[]): string {
    const sortedStops = [...stops].sort((a, b) => a.position - b.position);
    const stopXml = sortedStops.map(s => `    <stop offset="${s.position}%" stop-color="${s.color}" />`).join('\n');
    if (type === 'linear') {
        const rad = (angle - 90) * Math.PI / 180;
        const x1 = 0.5 - 0.5 * Math.cos(rad);
        const y1 = 0.5 - 0.5 * Math.sin(rad);
        const x2 = 0.5 + 0.5 * Math.cos(rad);
        const y2 = 0.5 + 0.5 * Math.sin(rad);
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">\n  <defs>\n    <linearGradient id="g" x1="${x1.toFixed(3)}" y1="${y1.toFixed(3)}" x2="${x2.toFixed(3)}" y2="${y2.toFixed(3)}">\n${stopXml}\n    </linearGradient>\n  </defs>\n  <rect width="100" height="100" fill="url(#g)" />\n</svg>`;
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">\n  <defs>\n    <radialGradient id="g">\n${stopXml}\n    </radialGradient>\n  </defs>\n  <rect width="100" height="100" fill="url(#g)" />\n</svg>`;
}

export default class GradientGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        type: 'linear',
        angle: 135,
        shape: 'ellipse',
        stops: [
            { color: '#6366f1', position: 0 },
            { color: '#ec4899', position: 100 },
        ],
        copyKind: null,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get gradient(): string {
        return buildGradient(this.state.type, this.state.angle, this.state.shape, this.state.stops);
    }

    private get cssCode(): string {
        return `background: ${this.gradient};`;
    }

    private get svgCode(): string {
        return buildSvg(this.state.type, this.state.angle, this.state.stops);
    }

    updateStop = (i: number, partial: Partial<Stop>) => {
        const stops = [...this.state.stops];
        stops[i] = { ...stops[i], ...partial };
        this.setState({ stops });
    };

    addStop = () => {
        const stops = [...this.state.stops];
        stops.push({ color: '#10b981', position: 50 });
        this.setState({ stops });
    };

    removeStop = (i: number) => {
        if (this.state.stops.length <= 2) return;
        const stops = this.state.stops.filter((_, idx) => idx !== i);
        this.setState({ stops });
    };

    copy = (kind: 'css' | 'svg') => {
        navigator.clipboard.writeText(kind === 'css' ? this.cssCode : this.svgCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copyKind: kind });
            this.copyTimeout = setTimeout(() => this.setState({ copyKind: null }), 2000);
        });
    };

    render() {
        const { type, angle, shape, stops, copyKind } = this.state;

        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Gradient type</span></div>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        {(['linear', 'radial', 'conic'] as GradientType[]).map(t => (
                            <button key={t}
                                className={`${genStyles.iconBtn} ${type === t ? genStyles.iconBtnPrimary : ''}`}
                                onClick={() => this.setState({ type: t })}>{t}</button>
                        ))}
                    </div>
                    {(type === 'linear' || type === 'conic') && (
                        <div className={genStyles.field}>
                            <label htmlFor="grad-angle">Angle ({angle}°)</label>
                            <input id="grad-angle" type="range" min={0} max={360} value={angle}
                                onChange={e => this.setState({ angle: +e.target.value })} />
                        </div>
                    )}
                    {type === 'radial' && (
                        <div className={genStyles.field}>
                            <label htmlFor="grad-shape">Shape</label>
                            <select id="grad-shape" value={shape}
                                onChange={e => this.setState({ shape: e.target.value as any })}>
                                <option value="ellipse">ellipse</option>
                                <option value="circle">circle</option>
                            </select>
                        </div>
                    )}
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Color stops</span></div>
                    {stops.map((s, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input type="color" value={s.color} style={{ width: 38, height: 32, border: '1px solid #e2e8f0', borderRadius: 6 }}
                                onChange={e => this.updateStop(i, { color: e.target.value })} />
                            <input type="text" value={s.color} style={{ flex: 1, padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid #e2e8f0', fontFamily: 'ui-monospace, monospace', fontSize: '0.85rem' }}
                                onChange={e => this.updateStop(i, { color: e.target.value })} />
                            <input type="number" inputMode="decimal" min={0} max={100} value={s.position}
                                style={{ width: 70, padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
                                onChange={e => this.updateStop(i, { position: Math.max(0, Math.min(100, +e.target.value || 0)) })} />
                            <span style={{ color: '#64748b', fontSize: '0.85rem' }}>%</span>
                            <button className={genStyles.removeBtn} onClick={() => this.removeStop(i)} disabled={stops.length <= 2}
                                aria-label="Remove stop">×</button>
                        </div>
                    ))}
                    <button className={genStyles.addBtn} onClick={this.addStop}>+ Add stop</button>
                </div>
            </>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Preview"
                previewMeta={type}
                preview={<div style={{ width: '100%', height: 200, borderRadius: 8, background: this.gradient }} />}
                codeLabel="CSS"
                codeMeta={`${this.cssCode.length} chars`}
                code={this.cssCode}
                codeActions={<>
                    <button className={`${genStyles.iconBtn} ${copyKind === 'css' ? genStyles.iconBtnSuccess : ''}`}
                        onClick={() => this.copy('css')}>
                        {copyKind === 'css' ? '✓ Copied' : '📋 Copy CSS'}
                    </button>
                    <button className={`${genStyles.iconBtn} ${copyKind === 'svg' ? genStyles.iconBtnSuccess : ''}`}
                        onClick={() => this.copy('svg')}>
                        {copyKind === 'svg' ? '✓ Copied' : '📋 Copy SVG'}
                    </button>
                </>}
            />
        );
    }
}
